"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { createClient } from "@/lib/supabase/client";
import { setLocalWatchlistGroup } from "@/lib/local-portfolio/store";

const GROUP_MAX_LENGTH = 40;
const DATALIST_ID = "watchlist-group-suggestions";

/**
 * Watchlist-Gruppen/Ordner zur eigenen Kategorisierung (backlog.md -> "Weiterer
 * Nutzwert-Katalog" -> Watchlist, z. B. "Dividenden-Kandidaten") - gleiches
 * Selbstständigkeits-Muster wie WatchlistStarButton (eigene Auth-Erkennung, verzweigt
 * intern zwischen Supabase-Update und lib/local-portfolio/store.ts statt einer
 * Prop-Vermittlung durch den Elternbaum). `currentGroup`/`existingGroups` kommen als
 * Props vom Grid (das ohnehin schon alle Gruppen für die Sektionsanzeige lädt) - spart
 * eine zusätzliche Abfrage nur für die Autovervollständigung.
 */
export function GroupAssignButton({
  symbol,
  currentGroup,
  existingGroups,
}: {
  symbol: string;
  currentGroup: string | undefined;
  existingGroups: string[];
}) {
  const router = useRouter();
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState(currentGroup ?? "");
  const [isPending, setIsPending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect), gleiches
    // Muster wie ScreenerLoadingModal/AcademyQuiz etc.
    const id = setTimeout(() => setDraft(currentGroup ?? ""), 0);
    return () => clearTimeout(id);
  }, [currentGroup]);

  async function handleSave() {
    const trimmed = draft.trim().slice(0, GROUP_MAX_LENGTH);
    const nextGroup = trimmed.length > 0 ? trimmed : null;
    if (nextGroup === (currentGroup ?? null)) {
      setOpen(false);
      return;
    }

    if (userId === null) {
      setLocalWatchlistGroup(symbol, nextGroup);
      setOpen(false);
      return;
    }
    if (!userId) return;

    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase
      .from("watchlists")
      .update({ group_name: nextGroup })
      .eq("symbol", symbol)
      .eq("user_id", userId);
    setIsPending(false);
    if (!error) {
      setOpen(false);
      router.refresh();
    }
  }

  if (userId === undefined) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={currentGroup ? `Gruppe: ${currentGroup}` : "Gruppe zuweisen"}
          title={currentGroup ? `Gruppe: ${currentGroup}` : "Gruppe zuweisen"}
        >
          <Tag className={currentGroup ? "size-4 text-gold-text" : "size-4"} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <p className="text-xs text-muted-foreground">
          Ordne {symbol} einer eigenen Gruppe zu (z.&nbsp;B. &bdquo;Dividenden-Kandidaten&rdquo;) - leer lassen, um
          die Zuordnung zu entfernen.
        </p>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="Gruppenname…"
          aria-label="Gruppenname"
          maxLength={GROUP_MAX_LENGTH}
          list={DATALIST_ID}
          className="bg-card"
        />
        <datalist id={DATALIST_ID}>
          {existingGroups.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
        <Button type="button" size="sm" onClick={handleSave} disabled={isPending}>
          Speichern
        </Button>
      </PopoverContent>
    </Popover>
  );
}
